import { useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import { useLang } from "@/i18n";
import { scrollToId } from "@/components/Navbar";

const HUBS = [
  { x: 0.13, y: 0.62, label: "Abidjan" },
  { x: 0.07, y: 0.4, label: "Dakar" },
  { x: 0.2, y: 0.68, label: "Douala" },
  { x: 0.28, y: 0.78, label: "Kigali" },
  { x: 0.44, y: 0.22, label: "Paris" },
  { x: 0.4, y: 0.13, label: "London" },
  { x: 0.62, y: 0.34, label: "Dubai" },
  { x: 0.5, y: 0.08, label: "Washington" },
  { x: 0.8, y: 0.58, label: "Singapore" },
  { x: 0.62, y: 0.05, label: "New York" },
];
const LINKS = [[0, 3], [1, 3], [2, 5], [0, 5], [3, 4], [3, 6], [5, 7], [6, 8], [2, 7], [2, 3], [1, 5]];

function Constellation() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, w, h, t0 = performance.now();
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = (now) => {
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);
      const pts = HUBS.map((p, i) => ({
        x: p.x * w + Math.sin(t * 0.25 + i * 1.7) * 6 * devicePixelRatio,
        y: p.y * h + Math.cos(t * 0.2 + i * 2.1) * 5 * devicePixelRatio,
        label: p.label,
      }));
      ctx.lineWidth = 0.6 * devicePixelRatio;
      LINKS.forEach(([a, b], i) => {
        const pulse = 0.12 + 0.1 * (0.5 + 0.5 * Math.sin(t * 0.6 + i));
        ctx.strokeStyle = `rgba(212,175,55,${pulse})`;
        ctx.beginPath();
        const mx = (pts[a].x + pts[b].x) / 2;
        const my = Math.min(pts[a].y, pts[b].y) - 28 * devicePixelRatio;
        ctx.moveTo(pts[a].x, pts[a].y);
        ctx.quadraticCurveTo(mx, my, pts[b].x, pts[b].y);
        ctx.stroke();
      });
      ctx.textAlign = "left";
      pts.forEach((p, i) => {
        const glow = 0.55 + 0.45 * Math.sin(t * 0.9 + i * 1.3);
        ctx.beginPath();
        ctx.fillStyle = `rgba(212,175,55,${0.5 + glow * 0.5})`;
        ctx.arc(p.x, p.y, (1.6 + glow) * devicePixelRatio, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "rgba(226,232,240,0.92)";
        ctx.font = `${10.5 * devicePixelRatio}px "JetBrains Mono", monospace`;
        ctx.shadowColor = "rgba(212,175,55,0.55)";
        ctx.shadowBlur = 6 * devicePixelRatio;
        ctx.fillText(p.label.toUpperCase(), p.x + 9 * devicePixelRatio, p.y + 3 * devicePixelRatio);
        ctx.shadowBlur = 0;
      });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} data-testid="hero-constellation" className="absolute inset-0 w-full h-full" />;
}

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-1, 1], [-16, 16]), { stiffness: 40, damping: 20 });
  const py = useSpring(useTransform(my, [-1, 1], [-10, 10]), { stiffness: 40, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section id="top" onMouseMove={onMove} className="relative min-h-[100svh] flex items-end overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 bg-obsidian" />
      <motion.div style={{ x: px, y: py }} className="absolute inset-[-40px]">
        <Constellation />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/70 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0C1226_85%)] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-xs font-mono uppercase tracking-[0.3em] text-gold/80 mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-[4.6rem] leading-[1.05] tracking-tight text-slate-100 max-w-5xl" data-testid="hero-title">
          <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.5} className="italic gold-text">{t.hero.line2}</MaskedLine>
          <MaskedLine delay={0.65}>{t.hero.line3}</MaskedLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 max-w-2xl"
        >
          <p className="text-base sm:text-lg text-slate-300 font-light leading-relaxed" data-testid="hero-subtitle">{t.hero.sub1}</p>
          <p className="mt-3 text-sm sm:text-base text-slate-400 font-light leading-relaxed">{t.hero.sub2}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-present-project"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-3 px-7 py-3.5 bg-gold text-obsidian text-sm font-medium tracking-wide rounded-full hover:bg-gold-light transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-explore-opportunity"
            onClick={() => scrollToId("opportunities")}
            className="group inline-flex items-center gap-3 px-7 py-3.5 border border-slate-600 text-slate-200 text-sm tracking-wide rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <Compass size={16} />
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.6 }}
          className="mt-16 text-[11px] font-mono uppercase tracking-[0.22em] text-slate-300"
          data-testid="hero-hubs"
        >
          {t.hero.hubs}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-3 text-slate-400"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] [writing-mode:vertical-lr]">{t.hero.scroll}</span>
        <div className="w-px h-14 bg-gradient-to-b from-gold/60 to-transparent animate-pulse-gold" />
      </motion.div>
    </section>
  );
}
